/**
 * Omni Auto-Add & Lock (v17)
 * - Ensures the Physical SIM Pack is present exactly once when any "triple-cut" physical SIM is in cart
 * - Locks the SIM Pack in the cart UI (no manual qty change, no manual removal)
 *
 * Note: Guided Installation / Setup & Onboarding enforcement is handled by assets/cart.js (V16a enforcer) and Liquid rules.
 *
 * Designed for Dawn. (cart drawer + cart page).
 */
(() => {
  if (window.__OMNI_AUTO_ADD_LOCK_V16A__) return;
  window.__OMNI_AUTO_ADD_LOCK_V16A__ = true;

  // -----------------------------
  // CONFIG
  // -----------------------------
  const SIM_PACK_VARIANT_ID = 52795246805259; // Physical SIM Pack (must stay at qty 1 when needed)
  const GUIDED_INSTALL_PHONE_VARIANT_ID = 52537855049995; // Phone System Guided Installation (qty must match extensions)

  const DEBOUNCE_MS = 350;

  // Fallback identification for phone system extensions (because /cart.js does not include tags)
  const PHONE_HANDLE_RE = /(omni-communicator-s|omni-flow)/i;
  const PHONE_DESC_RE = /(SETUP REQUIRED:\s*For phone systems|Guided Installation \(Remote\) is mandatory|Phone Systems require)/i;

  // -----------------------------
  // STATE
  // -----------------------------
  let timer = null;
  let isRunning = false;
  let internalMutationDepth = 0;
  let lastInternalMutationAt = 0;

  // -----------------------------
  // HELPERS
  // -----------------------------
  const now = () => Date.now();

  function log(...args) {
    // Toggle to true if you ever need verbose debugging
    const DEBUG = false;
    if (DEBUG) console.log("[OMNI v7]", ...args);
  }

  function isCartMutationUrl(url) {
    if (!url) return false;
    const u = String(url);
    // Note: includes add/change/update/clear endpoints (common Dawn patterns)
    return (
      u.includes("/cart/add") ||
      u.includes("/cart/change") ||
      u.includes("/cart/update") ||
      u.includes("/cart/clear")
    );
  }

  function scheduleReconcile(reason) {
    if (internalMutationDepth > 0) return; // ignore our own cart calls to avoid loops
    if (Date.now() - lastInternalMutationAt < 700) return; // ignore the immediate refresh after our own mutation
    clearTimeout(timer);
    timer = setTimeout(() => reconcile(reason), DEBOUNCE_MS);
  }

  async function fetchCart() {
    const res = await fetch(`/cart.js?ts=${now()}`, { credentials: "same-origin" });
    if (!res.ok) throw new Error(`Failed to fetch cart.js (${res.status})`);
    return res.json();
  }

  function findLineByVariantId(cart, variantId) {
    if (!cart || !Array.isArray(cart.items)) return null;
    return cart.items.find((it) => Number(it.variant_id || it.id) === Number(variantId)) || null;
  }

  function itemHasTripleCutOption(item) {
    // Most reliable: check options_with_values for "SIM Type" == "Triple Cut" (or close variants)
    try {
      if (Array.isArray(item.options_with_values)) {
        const simType = item.options_with_values.find((o) => (o?.name || "").toLowerCase().includes("sim"));
        if (simType && typeof simType.value === "string" && /triple\s*[-]?\s*cut/i.test(simType.value)) return true;
      }
    } catch (e) {}

    // Fallbacks
    const title = (item.title || "").toLowerCase();
    const props = item.properties || {};
    const propStr = JSON.stringify(props || {}).toLowerCase();
    return /triple\s*[-]?\s*cut/i.test(title) || /triple\s*[-]?\s*cut/i.test(propStr);
  }

  function cartNeedsSimPack(cart) {
    return Array.isArray(cart.items) && cart.items.some((it) => itemHasTripleCutOption(it));
  }

  function isPhoneSystemExtensionItem(item) {
    if (!item) return false;

    const vid = Number(item.variant_id || item.id);
    if (vid === Number(SIM_PACK_VARIANT_ID)) return false;
    if (vid === Number(GUIDED_INSTALL_PHONE_VARIANT_ID)) return false;

    const handle = (item.handle || "").toLowerCase();
    const title = (item.title || "").toLowerCase();

    // Strong signal: your own explicit line in the description
    if (PHONE_DESC_RE.test(item.product_description || "")) return true;

    // Reasonable fallback: handle naming convention
    if (PHONE_HANDLE_RE.test(handle)) return true;

    // Secondary fallback: title contains "phone system"
    if (title.includes("phone system")) return true;

    return false;
  }

  function phoneSystemExtensionsQty(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    return cart.items.reduce((sum, it) => sum + (isPhoneSystemExtensionItem(it) ? Number(it.quantity || 0) : 0), 0);
  }

  async function cartChangeByKey(key, quantity) {
    internalMutationDepth++;
    try {
      const res = await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ id: key, quantity }),
      });
      if (!res.ok) throw new Error(`cart/change.js failed (${res.status})`);
      return res.json();
    } finally {
      lastInternalMutationAt = Date.now();
      internalMutationDepth--;
    }
  }

  async function cartAddVariant(variantId, quantity) {
    internalMutationDepth++;
    try {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ items: [{ id: variantId, quantity }] }),
      });
      if (!res.ok) throw new Error(`cart/add.js failed (${res.status})`);
      return res.json();
    } finally {
      lastInternalMutationAt = Date.now();
      internalMutationDepth--;
    }
  }

  // -----------------------------
  // UI LOCKING (drawer + full cart)
  // -----------------------------
  function findRowByVariantId(variantId, root = document) {
    // Most Dawn links include ?variant=ID somewhere
    const link = root.querySelector(`a[href*="variant=${variantId}"]`);
    if (link) return link.closest(".cart-item") || link.closest("tr") || link.closest("li");
    // Fallback: sometimes a row has data-variant-id
    const byAttr = root.querySelector(`[data-variant-id="${variantId}"]`);
    if (byAttr) return byAttr.closest(".cart-item") || byAttr.closest("tr") || byAttr.closest("li");
    return null;
  }

  function lockRow(row, noteText) {
    if (!row || row.classList.contains("omni-locked")) return;
    row.classList.add("omni-locked");

    // Disable quantity controls
    row.querySelectorAll("button.quantity__button, button[name='plus'], button[name='minus']").forEach((btn) => {
      btn.setAttribute("disabled", "disabled");
      btn.classList.add("disabled");
      btn.style.pointerEvents = "none";
    });

    const qtyInput = row.querySelector('input[name="updates[]"]');
    if (qtyInput) {
      qtyInput.setAttribute("readonly", "readonly");
      qtyInput.setAttribute("disabled", "disabled");
      qtyInput.classList.add("disabled");
    }

    // Hide/disable remove controls
    row.querySelectorAll("cart-remove-button, a[href*='/cart/change'], button[name='remove'], button.cart-remove-button").forEach((el) => {
      el.setAttribute("aria-disabled", "true");
      el.style.pointerEvents = "none";
      // Prefer hiding only the visible button/link
      if (el.tagName === "A" || el.tagName === "BUTTON") el.style.display = "none";
    });

    // Add a small note (only once)
    const noteId = "omni-locked-note";
    if (noteText && !row.querySelector(`.${noteId}`)) {
      const note = document.createElement("div");
      note.className = noteId;
      note.style.marginTop = "0.5rem";
      note.style.fontSize = "0.85em";
      note.style.lineHeight = "1.2";
      note.textContent = noteText;

      // Try to place under quantity/price area
      const target =
        row.querySelector(".cart-item__quantity") ||
        row.querySelector(".cart-item__details") ||
        row;
      target.appendChild(note);
    }
  }

  function unlockRow(row) {
    if (!row || !row.classList.contains("omni-locked")) return;
    row.classList.remove("omni-locked");

    // Re-enable quantity controls (only if theme expects them)
    row.querySelectorAll("button.quantity__button, button[name='plus'], button[name='minus']").forEach((btn) => {
      btn.removeAttribute("disabled");
      btn.classList.remove("disabled");
      btn.style.pointerEvents = "";
    });

    const qtyInput = row.querySelector('input[name="updates[]"]');
    if (qtyInput) {
      qtyInput.removeAttribute("readonly");
      qtyInput.removeAttribute("disabled");
      qtyInput.classList.remove("disabled");
    }

    // Unhide remove controls (if we hid them)
    row.querySelectorAll("a[href*='/cart/change'], button[name='remove'], button.cart-remove-button").forEach((el) => {
      el.removeAttribute("aria-disabled");
      el.style.pointerEvents = "";
      if (el.tagName === "A" || el.tagName === "BUTTON") el.style.display = "";
    });

    // Remove note
    row.querySelectorAll(".omni-locked-note").forEach((n) => n.remove());
  }

  function applyLocks({ needsSimPack, needsGuidedInstall }) {
    // Full cart page
    const fullCartRoot = document.getElementById("main-cart-items") || document;
    const simRow = findRowByVariantId(SIM_PACK_VARIANT_ID, fullCartRoot);
    if (simRow && needsSimPack) lockRow(simRow, "SIM Pack is added automatically and stays at quantity 1.");
    if (simRow && !needsSimPack) unlockRow(simRow);

    const giRow = findRowByVariantId(GUIDED_INSTALL_PHONE_VARIANT_ID, fullCartRoot);
    if (giRow && needsGuidedInstall) {
      lockRow(
        giRow,
        ""
      );
    }
    if (giRow && !needsGuidedInstall) unlockRow(giRow);

    // Cart drawer (if present)
    const drawerRoot = document.getElementById("CartDrawer") || document.querySelector("cart-drawer") || document;
    const simRowDrawer = findRowByVariantId(SIM_PACK_VARIANT_ID, drawerRoot);
    if (simRowDrawer && needsSimPack) lockRow(simRowDrawer, "SIM Pack is added automatically and stays at quantity 1.");
    if (simRowDrawer && !needsSimPack) unlockRow(simRowDrawer);

    const giRowDrawer = findRowByVariantId(GUIDED_INSTALL_PHONE_VARIANT_ID, drawerRoot);
    if (giRowDrawer && needsGuidedInstall) {
      lockRow(
        giRowDrawer,
        ""
      );
    }
    if (giRowDrawer && !needsGuidedInstall) unlockRow(giRowDrawer);
  }

  // -----------------------------
  // DRAWER/BUBBLE REFRESH (WITHOUT FORCING OPEN)
  // -----------------------------
  function getSectionInnerHTML(html, selector = ".shopify-section") {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const el = doc.querySelector(selector);
    return el ? el.innerHTML : "";
  }

  async function refreshDrawerAndBubble(cart) {
    // Only refresh if the relevant containers exist
    const hasDrawer = !!document.getElementById("CartDrawer") || !!document.querySelector("cart-drawer");
    const bubble = document.getElementById("cart-icon-bubble");
    if (!hasDrawer && !bubble) return;

    const res = await fetch(`/?sections=cart-drawer,cart-icon-bubble&ts=${now()}`, { credentials: "same-origin" });
    if (!res.ok) return;
    const sections = await res.json();

    // Update bubble
    if (bubble && sections["cart-icon-bubble"]) {
      bubble.innerHTML = getSectionInnerHTML(sections["cart-icon-bubble"]);
    }

    // Update drawer contents without forcing open()
    const drawer = document.getElementById("CartDrawer");
    if (drawer && sections["cart-drawer"]) {
      const newHtml = getSectionInnerHTML(sections["cart-drawer"], "#CartDrawer");
      drawer.innerHTML = newHtml;
    }

    // Re-apply locks after re-render
    const needsSimPack = cartNeedsSimPack(cart);
    const needsGuidedInstall = phoneSystemExtensionsQty(cart) > 0;
    applyLocks({ needsSimPack, needsGuidedInstall });
  }

  // -----------------------------
  // CORE RECONCILE
  // -----------------------------
  async function reconcile(reason) {
    if (isRunning) return;
    isRunning = true;

    try {
      const cart = await fetchCart();

      const needsSimPack = cartNeedsSimPack(cart);
      const simLine = findLineByVariantId(cart, SIM_PACK_VARIANT_ID);

      const requiredGuidedQty = phoneSystemExtensionsQty(cart);
      const needsGuidedInstall = requiredGuidedQty > 0;
      const guidedLine = findLineByVariantId(cart, GUIDED_INSTALL_PHONE_VARIANT_ID);

      log("reconcile", reason, { needsSimPack, simLineQty: simLine?.quantity, requiredGuidedQty, guidedQty: guidedLine?.quantity });

      // ---- SIM PACK RULE: exists iff any triple-cut SIM exists, and qty must be 1
      if (needsSimPack) {
        if (!simLine) {
          await cartAddVariant(SIM_PACK_VARIANT_ID, 1);
        } else if (Number(simLine.quantity) !== 1) {
          await cartChangeByKey(simLine.key, 1);
        }
      } else {
        if (simLine) {
          await cartChangeByKey(simLine.key, 0);
        }
      }

      // (V16a) Guided Installation / Setup & Onboarding is enforced elsewhere.

      // Refresh UI & locks (drawer/bubble especially)
      const freshCart = await fetchCart();
      applyLocks({ needsSimPack: cartNeedsSimPack(freshCart), needsGuidedInstall: false });
      refreshDrawerAndBubble(freshCart).catch(() => {});
    } catch (e) {
      console.error("[OMNI v7] reconcile failed:", e);
    } finally {
      isRunning = false;
    }
  }

  // -----------------------------
  // HOOKS (V17): event-driven, no cart:refresh loop
  // -----------------------------
  window.addEventListener('DOMContentLoaded', () => {
    scheduleReconcile('DOMContentLoaded');
  });

  window.addEventListener('pageshow', () => {
    scheduleReconcile('pageshow');
  });

  window.addEventListener('cart:updated', (ev) => {
    const source = ev && ev.detail && ev.detail.source ? String(ev.detail.source) : '';
    // Ignore self-originated updates.
    if (source === 'omni-sim-pack') return;
    scheduleReconcile('cart:updated');
  });
})();/* VoIPandSims cart label: show ONE-OFF CHARGE under Setup & Onboarding quantity boxes */
(function () {
  function addOneOffChargeLabels() {
    const cartItems = document.querySelectorAll('.cart-item, tr.cart-item, [id^="CartItem-"]');

    cartItems.forEach(function (item) {
      const itemText = (item.innerText || '').toLowerCase();

      const isSetupOnboarding =
        itemText.includes('setup & onboarding') ||
        itemText.includes('setup and onboarding');

      if (!isSetupOnboarding) return;

      const quantityCell =
        item.querySelector('.cart-item__quantity') ||
        item.querySelector('.cart-item__quantity-wrapper') ||
        item.querySelector('quantity-popover') ||
        item.querySelector('quantity-input') ||
        item.querySelector('input[name="updates[]"]')?.closest('td') ||
        item.querySelector('input[name="updates[]"]')?.parentElement;

      if (!quantityCell) return;

      if (quantityCell.querySelector('.vns-one-off-charge-label')) return;

      const label = document.createElement('div');
      label.className = 'vns-one-off-charge-label';
      label.textContent = 'ONE-OFF CHARGE';
      label.style.marginTop = '6px';
      label.style.fontSize = '11px';
      label.style.fontWeight = '700';
      label.style.letterSpacing = '0.08em';
      label.style.textTransform = 'uppercase';
      label.style.lineHeight = '1.3';

      quantityCell.appendChild(label);
    });
  }

  document.addEventListener('DOMContentLoaded', addOneOffChargeLabels);

  document.addEventListener('cart:updated', function () {
    setTimeout(addOneOffChargeLabels, 150);
  });

  document.addEventListener('change', function () {
    setTimeout(addOneOffChargeLabels, 250);
  });

  const observer = new MutationObserver(function () {
    addOneOffChargeLabels();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
})();
